import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import toast from 'react-hot-toast';
import { initDemoMode } from './localdb';
import { supabase } from './supabase';

interface AppContextType {
  isOnline: boolean;
  isDemoMode: boolean;
  enableDemoMode: () => void;
  disableDemoMode: () => void;
  isSupabaseConnected: boolean;
}

const AppContext = createContext<AppContextType>({
  isOnline: true,
  isDemoMode: false,
  enableDemoMode: () => {},
  disableDemoMode: () => {},
  isSupabaseConnected: false
});

export const useAppContext = () => useContext(AppContext);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isDemoMode, setIsDemoMode] = useState(localStorage.getItem('demoMode') === 'true');
  const [isSupabaseConnected, setIsSupabaseConnected] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Connexion internet rétablie');
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.error('Connexion internet perdue');
      
      // Si passage en mode hors-ligne, proposer le mode démo
      if (!isDemoMode) {
        toast.custom((t) => (
          <div className="bg-white rounded-lg shadow-md p-4 max-w-md">
            <p className="font-semibold mb-2">Mode hors-ligne détecté</p>
            <p className="mb-3 text-sm">Voulez-vous activer le mode hors-ligne avec des données de démonstration ?</p>
            <div className="flex justify-end space-x-2">
              <button 
                onClick={() => {
                  toast.dismiss(t.id);
                }} 
                className="px-3 py-1 bg-gray-200 rounded-md"
              >
                Non
              </button>
              <button 
                onClick={() => {
                  enableDemoMode();
                  toast.dismiss(t.id);
                }} 
                className="px-3 py-1 bg-blue-500 text-white rounded-md"
              >
                Activer
              </button>
            </div>
          </div>
        ), { duration: 10000 });
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Test de connexion à Supabase
    const checkSupabaseConnection = async () => {
      try {
        // Utiliser en priorité l'API définie par défaut, sans chercher dans localStorage
        const apiUrl = import.meta.env.VITE_SUPABASE_URL;
        const apiKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        
        const { createClient } = await import('@supabase/supabase-js');
        const testClient = createClient(apiUrl, apiKey, {
          auth: {
            persistSession: false,
            autoRefreshToken: false,
          }
        });
        
        const { data, error } = await testClient
          .from('lotismode_products')
          .select('id')
          .limit(1);
        
        if (error) {
          console.error('Erreur de connexion à Supabase:', error);
          setIsSupabaseConnected(false);
          
          // Si on ne peut pas se connecter à Supabase, proposer le mode démo
          if (!isDemoMode) {
            toast.custom((t) => (
              <div className="bg-white rounded-lg shadow-md p-4 max-w-md">
                <p className="font-semibold mb-2">Erreur de connexion au serveur</p>
                <p className="mb-3 text-sm">Impossible de se connecter à la base de données. Voulez-vous activer le mode hors-ligne avec des données de démonstration ?</p>
                <div className="flex justify-end space-x-2">
                  <button 
                    onClick={() => {
                      toast.dismiss(t.id);
                    }} 
                    className="px-3 py-1 bg-gray-200 rounded-md"
                  >
                    Non
                  </button>
                  <button 
                    onClick={() => {
                      enableDemoMode();
                      toast.dismiss(t.id);
                    }} 
                    className="px-3 py-1 bg-blue-500 text-white rounded-md"
                  >
                    Activer
                  </button>
                </div>
              </div>
            ), { duration: 10000 });
          }
        } else {
          setIsSupabaseConnected(true);
        }
      } catch (error) {
        console.error('Erreur lors du test de connexion à Supabase:', error);
        setIsSupabaseConnected(false);
      }
    };

    checkSupabaseConnection();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isDemoMode]);

  const enableDemoMode = async () => {
    try {
      await initDemoMode();
      setIsDemoMode(true);
      localStorage.setItem('demoMode', 'true');
      toast.success('Mode hors-ligne activé');
      
      // Rafraîchir la page pour appliquer le mode hors ligne
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Erreur lors de l\'activation du mode démo:', error);
      toast.error('Erreur lors de l\'activation du mode hors-ligne');
    }
  };

  const disableDemoMode = () => {
    setIsDemoMode(false);
    localStorage.removeItem('demoMode');
    toast.success('Mode hors-ligne désactivé');
    
    // Rafraîchir la page pour appliquer le mode en ligne
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  return (
    <AppContext.Provider 
      value={{ 
        isOnline, 
        isDemoMode, 
        enableDemoMode, 
        disableDemoMode,
        isSupabaseConnected
      }}
    >
      {children}
    </AppContext.Provider>
  );
};